import React, { useCallback, useEffect, useState } from "react";
import { useTabs } from "../../hooks/lib/useTabs";
import { Link } from "react-router-dom";
import { useMobile } from "../../hooks/custom/useMobile";
import {
  IJoinAsFacultyData,
  IJoinAsFacultyDetailsData,
} from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { dynamicFailed } from "../../hooks/custom/useToast";
import {
  fetchAreasMajorsTab,
  fetchWhoIsUNepalFor,
  fetchWhyJoinUNepal,
} from "../../api/JoinAsFacultyApi";
import { getVisibleTabs, truncateText } from "../../exports/functions";
import TabSidebar from "../TabSidebar";

export const JoinFaculty = ({
  joinAsFacultyDetails,
}: {
  joinAsFacultyDetails: IJoinAsFacultyDetailsData[];
}) => {
  useTabs();

  const isMobile = useMobile();
  const { setLoading } = useLoader();

  const [activeTab, setActiveTab] = useState<string>("#td_tab_1");

  const [openSidebar, setOpenSidebar] = useState(false);

  const [allTabData, setAllTabData] = useState<{
    [key: string]: IJoinAsFacultyData[];
  }>({});

  const fetchAllTabData = useCallback(async () => {
    try {
      setLoading(true);

      const [areasMajors, whoIsUNepalFor, whyJoinUNepal] = await Promise.all([
        fetchAreasMajorsTab(),
        fetchWhoIsUNepalFor(),
        fetchWhyJoinUNepal(),
      ]);

      setAllTabData({
        "#td_tab_1": areasMajors,
        "#td_tab_2": whoIsUNepalFor,
        "#td_tab_4": whyJoinUNepal,
      });
    } catch (error: any) {
      dynamicFailed(error.response?.data?.error?.message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [setLoading]);

  useEffect(() => {
    fetchAllTabData();
  }, [fetchAllTabData]);

  const handleTabClick = (hash: string) => {
    setActiveTab(hash);
  };

  const allTabs = React.useMemo(
    () => [
      {
        id: "#td_tab_1",
        label: "Academic Areas & Majors",
      },
      {
        id: "#td_tab_2",
        label: "Who is UNepal for?",
      },
      {
        id: "#td_tab_4",
        label: "Why Join UNepal?",
      },
    ],
    [],
  );

  const visibleTabs = React.useMemo(() => {
    return getVisibleTabs(allTabs, activeTab, isMobile ? 2 : 4);
  }, [allTabs, activeTab, isMobile]);

  const tabData = allTabData[activeTab] || [];

  const renderTabContent = () => (
    <>
      <div
        style={{
          display: "flex",
          gap: "25px",
          justifyContent: "space-between",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <p className="mb-0 custom-text-p">
          {tabData.map((data, index) => (
            <React.Fragment key={index}>
              <span
                dangerouslySetInnerHTML={{
                  __html: data?.description,
                }}
              />

              {index !== tabData.length - 1 && (
                <div className="td_height_30 td_height_lg_30" />
              )}
            </React.Fragment>
          ))}
        </p>
      </div>

      <div className="td_height_50" />

      <div style={{ display: "flex", justifyContent: "center" }}>
        <Link
          to="/contact"
          className="td_btn td_style_1 td_radius_10 custom-td_style_1 td_medium"
        >
          <span className="td_btn_in custom_td_btn_in">
            <span>For More Information, Please Contact Us Now</span>

            <svg
              width="19"
              height="20"
              viewBox="0 0 19 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.1575 4.34302L3.84375 15.6567"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>

              <path
                d="M15.157 11.4142C15.157 11.4142 16.0887 5.2748 15.157 4.34311C14.2253 3.41142 8.08594 4.34314 8.08594 4.34314"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </span>
        </Link>
      </div>
    </>
  );

  return (
    <>
      <section>
        <div className="td_height_120 td_height_lg_40" />

        <div className="container">
          <div
            className="td_section_heading td_style_1 text-center wow fadeInUp"
            data-wow-duration="1s"
            data-wow-delay="0.15s"
          >
            <p
              className="td_section_subtitle_up td_fs_18 td_semibold td_spacing_1 td_mb_10 text-uppercase td_accent_color"
              style={{ textAlign: "unset" }}
            >
              COMMUNITY
            </p>

            <h2 className="td_section_title td_fs_48 mb-0">
              Join UNepal as Faculty or Staff
            </h2>

            <div className="td_height_30 td_height_lg_30" />
          </div>

          <p
            className="td_fs_18 mb-0"
            dangerouslySetInnerHTML={{
              __html: joinAsFacultyDetails[0]?.description,
            }}
          />

          <div className="td_height_80 td_height_lg_40" />

          <div className="row td_gap_y_50">
            <div className="td_course_details">
              <div className="td_tabs td_style_1 td_mb_50">
                <ul className="td_tab_links td_style_2 td_type_2 td_mp_0 td_medium td_fs_16 td_heading_color align-items-center justify-content-between">
                  <div className="td_tab_links td_style_2">
                    {visibleTabs.map((tab) => (
                      <li
                        key={tab.id}
                        className={activeTab === tab.id ? "active" : ""}
                      >
                        <a
                          href={tab.id}
                          onClick={() => handleTabClick(tab.id)}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title={tab.label}
                        >
                          {truncateText(tab.label, isMobile ? 10 : 30)}
                        </a>
                      </li>
                    ))}
                  </div>
                  {isMobile && (
                    <li>
                      <div className="course_sidebar_toggle_wrapper">
                        <button
                          className="course_sidebar_toggle mt-1"
                          onClick={() => setOpenSidebar(true)}
                        >
                          <span></span>
                          <span></span>
                          <span></span>
                          {/* <span></span>
                                  <span></span>
                                  <span></span> */}
                        </button>
                      </div>
                    </li>
                  )}
                </ul>

                <div className="td_tab_body td_fs_18">
                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_1" && "active_custom_td_tab"}`}
                    id="td_tab_1"
                  >
                    {activeTab === "#td_tab_1" && renderTabContent()}
                  </div>

                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_2" && "active_custom_td_tab"}`}
                    id="td_tab_2"
                  >
                    {activeTab === "#td_tab_2" && renderTabContent()}
                  </div>

                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_3" && "active_custom_td_tab"}`}
                    id="td_tab_3"
                  >
                    <p className="mb-0"></p>
                  </div>

                  <div
                    className={`td_tab active custom_td_tab ${activeTab === "#td_tab_4" && "active_custom_td_tab"}`}
                    id="td_tab_4"
                  >
                    {activeTab === "#td_tab_4" && renderTabContent()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <TabSidebar
        title="Join Faculty Tabs"
        tabs={allTabs}
        activeTab={activeTab}
        handleTabClick={handleTabClick}
        openSidebar={openSidebar}
        setOpenSidebar={setOpenSidebar}
      />
    </>
  );
};
